import MultiComboboxField from './MultiComboboxField';
import SearchableOptionsField from './SearchableOptionsField';
import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  FormHelperText,
  Button,
  IconButton,
  CircularProgress,
  Chip,
  Checkbox,
  FormControlLabel,
  FormGroup,
  FormLabel,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import FlexService from '../../services/FlexService';
import { enterAdvanceProps } from '../../utils/enterAdvance';
import { humanizeEntityName } from '../../utils/entityLabels';

/**
 * EntityEditOverlay - Full-workspace modal for editing a single entity row.
 * Driven entirely by EntityFieldMetadata from the backend.
 *
 * Covers the workspace area, has close button, cancel/save footer.
 * Fields with is_editable=false render as read-only.
 * On save, returns only changed fields.
 */
const EntityEditOverlay = ({ entityName, rowData, readOnly, colors, onSave, onClose }) => {
  const [fields, setFields] = useState([]);
  const [formValues, setFormValues] = useState({});
  const [originalValues, setOriginalValues] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});
  const [dynamicOptions, setDynamicOptions] = useState({}); // { fieldName: [{value, label}] } from optionsScript
  const [entityDisplayName, setEntityDisplayName] = useState(''); // entity_registry displayName from the fields API
  const entityLabel = entityDisplayName || humanizeEntityName(entityName);

  // Fetch field metadata
  useEffect(() => {
    const fetchFields = async () => {
      setLoading(true);
      try {
        const token = localStorage.getItem('waide_access_token');
        const res = await fetch(`/api/v1/catalog/${entityName}/fields`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) {
          const data = await res.json();
          setEntityDisplayName(data.displayName || '');
          const allFields = (data.fields || data || [])
            .filter((f) => f.isVisibleInForm !== false)
            .sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0));
          setFields(allFields);

          // Initialize form values from row data. Same bug class as the save-side fix
          // below: rowData is the raw grid row, keyed by whatever item_list.flex-style
          // loadScripts actually return (WMS's real field name, e.g.
          // reorderLevelQuantity), not the FAST entity's logical fieldName
          // (reorderLevelQty) -- without this fallback the field opened blank even
          // when WMS genuinely had a value.
          const initial = {};
          allFields.forEach((f) => {
            let wmsKey = f.wmsPayloadPath;
            if (wmsKey) {
              const bracketIdx = wmsKey.indexOf('[].');
              if (bracketIdx >= 0) wmsKey = wmsKey.substring(bracketIdx + 3);
            }
            initial[f.fieldName] = rowData?.[f.fieldName] ?? (wmsKey ? rowData?.[wmsKey] : undefined) ?? f.defaultValue ?? '';
          });
          setFormValues(initial);
          setOriginalValues(initial);
        }
      } catch (err) {
        console.error('Failed to fetch field metadata:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchFields();
  }, [entityName, rowData]);

  // Load dynamic options for MULTISELECT fields with an optionsScript (e.g. the
  // User form's "roles" field) -- mirrors EntityWizardOverlay's equivalent effect.
  useEffect(() => {
    const scriptsToLoad = fields.filter((f) => (f.fieldType === 'MULTISELECT' || f.fieldType === 'DROPDOWN' || f.fieldType === 'AUTOCOMPLETE' || f.fieldType === 'MULTICOMBOBOX' || f.fieldType === 'CHECKBOX_GROUP') && f.optionsScript);
    if (scriptsToLoad.length === 0) return;

    let cancelled = false;
    const loadAll = async () => {
      const results = {};
      await Promise.all(
        scriptsToLoad.map(async (f) => {
          try {
            const response = await FlexService.executeScript({
              scriptName: f.optionsScript,
              context: { _payload: rowData || {} },
            });
            if (response.success) {
              const ctx = response.context || {};
              const opts = ctx.result || ctx.data || [];
              if (Array.isArray(opts)) {
                results[f.fieldName] = opts;
              }
            }
          } catch (err) {
            console.error(`Failed to load options from script "${f.optionsScript}" for field "${f.fieldName}":`, err);
          }
        })
      );
      if (!cancelled && Object.keys(results).length > 0) {
        setDynamicOptions((prev) => ({ ...prev, ...results }));
      }
    };
    loadAll();
    return () => { cancelled = true; };
  }, [fields, rowData]);

  const applyTextTransform = useCallback((fieldName, value) => {
    if (!value || typeof value !== 'string') return value;
    const field = fields.find((f) => f.fieldName === fieldName);
    if (!field?.textTransform) return value;
    if (field.textTransform === 'UPPERCASE') return value.toUpperCase();
    if (field.textTransform === 'LOWERCASE') return value.toLowerCase();
    return value;
  }, [fields]);

  const handleChange = useCallback((fieldName, value) => {
    const transformed = applyTextTransform(fieldName, value);
    setFormValues((prev) => ({ ...prev, [fieldName]: transformed }));
    setErrors((prev) => {
      const next = { ...prev };
      delete next[fieldName];
      return next;
    });
  }, [applyTextTransform]);

  const handleSave = useCallback(() => {
    // Validate required fields
    const newErrors = {};
    fields.forEach((f) => {
      if (f.isRequired && f.isEditable !== false) {
        const val = formValues[f.fieldName];
        if (val === undefined || val === null || val === '') {
          newErrors[f.fieldName] = `${f.displayLabel} is required`;
        }
      }
    });
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    // Collect only changed fields
    const changedFields = {};
    const jsonParseErrors = {};
    Object.keys(formValues).forEach((key) => {
      if (formValues[key] !== originalValues[key]) {
        let value = formValues[key];
        // For TEXTAREA fields whose original value was an object, the user has been
        // editing a JSON string. Parse it back so the backend receives a Map/List
        // (which InternalEntityService converts to JSONB). Bad JSON is reported.
        const fieldDef = fields.find((f) => f.fieldName === key);
        const wasJsonShape = originalValues[key] !== null && typeof originalValues[key] === 'object';
        if (fieldDef?.fieldType === 'TEXTAREA' && wasJsonShape && typeof value === 'string') {
          try {
            value = JSON.parse(value);
          } catch (e) {
            jsonParseErrors[key] = `Invalid JSON: ${e.message}`;
          }
        }
        // FH-173: a cleared number is null, never "" (WMS number parsing throws on it).
        if ((fieldDef?.fieldType === 'NUMBER' || fieldDef?.fieldType === 'DECIMAL') && value === '') {
          value = null;
        }
        changedFields[key] = value;
      }
    });

    if (Object.keys(jsonParseErrors).length > 0) {
      setErrors(jsonParseErrors);
      return;
    }

    if (Object.keys(changedFields).length === 0) {
      onClose();
      return;
    }

    // REAL BUG FOUND AND FIXED 2026-10-06 (FH-49 investigation): this overlay never
    // applied wmsPayloadPath remapping at all, unlike EntityWizardOverlay.js's Create
    // flow -- any field whose FAST field name differs from WMS's real field name
    // (e.g. Item.reorderLevelQty -> WMS's reorderLevelQuantity, Area.maxLoadsPerLocation
    // -> WMS's maximumLoad) silently failed to persist when edited via this plain Edit
    // form, since the submitted payload used the FAST name as the key and WMS's
    // Update endpoint simply never recognized it. Confirmed live: editing
    // reorderLevelQty sent {reorderLevelQty: 15} and WMS's response came back with
    // reorderLevelQuantity still null. Remap each changed field's key through
    // wmsPayloadPath (falling back to the plain fieldName when none is set, and
    // stripping any "arrayKey[]." batch-line prefix the same way the wizard does, in
    // case a line entity is ever edited singly through this overlay).
    const wmsMappedFields = {};
    Object.keys(changedFields).forEach((key) => {
      const fieldDef = fields.find((f) => f.fieldName === key);
      let path = fieldDef?.wmsPayloadPath || key;
      const bracketIdx = path.indexOf('[].');
      if (bracketIdx >= 0) {
        path = path.substring(bracketIdx + 3);
      }
      wmsMappedFields[path] = changedFields[key];
    });

    setSaving(true);
    onSave(wmsMappedFields);
  }, [fields, formValues, originalValues, onSave, onClose]);

  const renderField = (field) => {
    const value = formValues[field.fieldName] ?? '';
    // Generic cross-field gate -- mirrors EntityWizardOverlay's equivalent logic
    // (FH-56, 2026-10-05).
    const dependencyUnmet = field.enabledWhenField
      && String(formValues[field.enabledWhenField]) !== String(field.enabledWhenValue ?? 'true');
    const isDisabled = readOnly || field.isEditable === false || dependencyUnmet;
    const error = errors[field.fieldName];

    const commonSx = {
      mb: 2,
      '& .MuiOutlinedInput-root': {
        borderRadius: 0,
        bgcolor: isDisabled ? colors.surfaceElevated : colors.background,
        '& fieldset': {
          borderColor: error ? '#ef4444' : colors.border,
        },
        '&:hover fieldset': {
          borderColor: isDisabled ? colors.border : '#22c55e',
        },
        '&.Mui-focused fieldset': {
          borderColor: '#22c55e',
        },
      },
      '& .MuiInputBase-input': { color: colors.textPrimary },
      '& .MuiInputLabel-root': { color: colors.textSecondary },
    };

    switch (field.fieldType) {
      case 'STATUS':
        return (
          <Box key={field.fieldName} sx={{ mb: 2 }}>
            <Typography variant="caption" sx={{ color: colors.textSecondary, mb: 0.5, display: 'block' }}>
              {field.displayLabel}
            </Typography>
            <Chip
              label={value}
              size="small"
              sx={{
                borderRadius: 0,
                bgcolor: `rgba(34, 197, 94, 0.15)`,
                color: '#22c55e',
                fontWeight: 600,
              }}
            />
          </Box>
        );

      case 'MULTICOMBOBOX':
        return (
          <MultiComboboxField
            key={field.fieldName}
            field={field}
            value={value}
            options={dynamicOptions[field.fieldName] || []}
            onChange={handleChange}
            disabled={isDisabled}
            error={error}
            colors={colors}
          />
        );

      case 'DROPDOWN':
        // the field's options script owns the list; fixed allowedValues are only the fallback
        const options = (field.optionsScript && dynamicOptions[field.fieldName])
          || (field.allowedValues
            ? (typeof field.allowedValues === 'string' ? JSON.parse(field.allowedValues) : field.allowedValues)
            : []);
        return (
          <FormControl key={field.fieldName} fullWidth size="small" required={!!field.isRequired} sx={{ ...commonSx }}>
            <InputLabel>{field.displayLabel}</InputLabel>
            <Select
              value={value}
              onChange={(e) => handleChange(field.fieldName, e.target.value)}
              label={field.displayLabel}
              disabled={isDisabled}
              sx={{
                color: colors.textPrimary,
                '& .MuiOutlinedInput-notchedOutline': { borderColor: colors.border, borderRadius: 0 },
              }}
            >
              {options.map((opt) => {
                const optValue = typeof opt === 'object' ? opt.value : opt;
                const optLabel = typeof opt === 'object' ? opt.label : opt;
                return (
                  <MenuItem key={optValue} value={optValue}>
                    {optLabel}
                  </MenuItem>
                );
              })}
            </Select>
            {error && <FormHelperText sx={{ color: '#ef4444' }}>{error}</FormHelperText>}
            {field.description && !error && (
              <FormHelperText sx={{ color: colors.textMuted }}>{field.description}</FormHelperText>
            )}
          </FormControl>
        );

      case 'MULTISELECT': {
        const options = dynamicOptions?.[field.fieldName] || [];
        // rowData seeds this from an array (e.g. roles: ["ADMIN"]); a brand-new
        // row has no rowData so the field starts as '' like every other field --
        // coerce that to an empty selection instead of crashing Select.
        const selected = Array.isArray(value) ? value : [];
        return (
          <FormControl key={field.fieldName} fullWidth size="small" required={!!field.isRequired} sx={{ ...commonSx }}>
            <InputLabel>{field.displayLabel}</InputLabel>
            <Select
              multiple
              value={selected}
              onChange={(e) => handleChange(field.fieldName, e.target.value)}
              label={field.displayLabel}
              disabled={isDisabled}
              renderValue={(sel) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {sel.map((v) => {
                    const opt = options.find((o) => o.value === v);
                    return <Chip key={v} label={opt?.label || v} size="small" sx={{ borderRadius: 0 }} />;
                  })}
                </Box>
              )}
              sx={{
                color: colors.textPrimary,
                '& .MuiOutlinedInput-notchedOutline': { borderColor: colors.border, borderRadius: 0 },
              }}
            >
              {options.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  <Checkbox checked={selected.includes(opt.value)} size="small" />
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
            {error && <FormHelperText sx={{ color: '#ef4444' }}>{error}</FormHelperText>}
            {field.description && !error && (
              <FormHelperText sx={{ color: colors.textMuted }}>{field.description}</FormHelperText>
            )}
          </FormControl>
        );
      }

      case 'CHECKBOX_GROUP': {
        // Same multi-value semantics as MULTISELECT, flattened into inline
        // checkboxes -- mirrors EntityWizardOverlay's equivalent case (FH-56,
        // 2026-10-05).
        const options = dynamicOptions?.[field.fieldName] || [];
        const selected = Array.isArray(value) ? value : [];
        const toggle = (optValue) => {
          const next = selected.includes(optValue)
            ? selected.filter((v) => v !== optValue)
            : [...selected, optValue];
          handleChange(field.fieldName, next);
        };
        return (
          <Box key={field.fieldName} sx={{ mb: 2 }}>
            <FormLabel required={!!field.isRequired} sx={{ fontSize: '0.8rem', color: colors.textSecondary, display: 'block', mb: 0.5 }}>
              {field.displayLabel}
            </FormLabel>
            <FormGroup row>
              {options.map((opt) => {
                const optValue = typeof opt === 'object' ? opt.value : opt;
                const optLabel = typeof opt === 'object' ? opt.label : opt;
                return (
                  <FormControlLabel
                    key={optValue}
                    control={
                      <Checkbox
                        size="small"
                        checked={selected.includes(optValue)}
                        onChange={() => toggle(optValue)}
                        disabled={isDisabled}
                      />
                    }
                    label={<Typography variant="body2" sx={{ color: colors.textPrimary }}>{optLabel}</Typography>}
                  />
                );
              })}
            </FormGroup>
            {error && <FormHelperText sx={{ color: '#ef4444' }}>{error}</FormHelperText>}
            {field.description && !error && (
              <FormHelperText sx={{ color: colors.textMuted }}>{field.description}</FormHelperText>
            )}
          </Box>
        );
      }

      case 'TEXTAREA': {
        // JSONB values arrive as objects/arrays (or as the PG driver's {type:'jsonb', value:'<json>'}
        // wrapper). Show pretty JSON in the textarea so the user can read and edit them.
        // The save handler parses valid JSON back before sending; invalid JSON is sent as-is
        // and rejected by the backend.
        const isPgWrapper = value && typeof value === 'object' && value.type === 'jsonb' && typeof value.value === 'string';
        let unwrapped = value;
        if (isPgWrapper) {
          try { unwrapped = JSON.parse(value.value); }
          catch (e) { unwrapped = value.value; }
        }
        const isJsonShape = unwrapped !== null && typeof unwrapped === 'object';
        const displayValue = isJsonShape ? JSON.stringify(unwrapped, null, 2) : (unwrapped ?? '');
        return (
          <TextField
            key={field.fieldName}
            fullWidth
            size="small"
            required={!!field.isRequired}
            label={field.displayLabel}
            value={displayValue}
            onChange={(e) => handleChange(field.fieldName, e.target.value)}
            disabled={isDisabled}
            multiline
            minRows={isJsonShape ? 8 : 3}
            maxRows={20}
            error={!!error}
            helperText={error || field.description}
            sx={{
              ...commonSx,
              ...(isJsonShape && {
                '& .MuiInputBase-input': { fontFamily: 'monospace', fontSize: '0.78rem' },
              }),
            }}
          />
        );
      }

      case 'NUMBER':
      case 'DECIMAL':
        // Send a real JS number, not the raw string from the DOM input --
        // WMS fields backing these (e.g. Location.weightCapacity/slotWidth,
        // typed BigDecimal) are set via direct property assignment on the
        // Grails side, which doesn't auto-convert a String the way its data
        // binder would, and throws a ClassCastException on save.
        return (
          <TextField
            key={field.fieldName}
            fullWidth
            size="small"
            required={!!field.isRequired}
            type="number"
            label={field.displayLabel}
            value={value}
            onChange={(e) => {
              const raw = e.target.value;
              handleChange(field.fieldName, raw === '' ? '' : Number(raw));
            }}
            disabled={isDisabled}
            error={!!error}
            helperText={error || field.description}
            inputProps={{ min: field.minValue, max: field.maxValue, step: field.fieldType === 'DECIMAL' ? 'any' : 1 }}
            sx={commonSx}
          />
        );

      case 'DATE':
        return (
          <TextField
            key={field.fieldName}
            fullWidth
            size="small"
            required={!!field.isRequired}
            type="date"
            label={field.displayLabel}
            value={value}
            onChange={(e) => handleChange(field.fieldName, e.target.value)}
            disabled={isDisabled}
            error={!!error}
            helperText={error || field.description}
            InputLabelProps={{ shrink: true }}
            sx={commonSx}
          />
        );

      case 'DATETIME':
        return (
          <TextField
            key={field.fieldName}
            fullWidth
            size="small"
            required={!!field.isRequired}
            type="datetime-local"
            label={field.displayLabel}
            value={value}
            onChange={(e) => handleChange(field.fieldName, e.target.value)}
            disabled={isDisabled}
            error={!!error}
            helperText={error || field.description}
            InputLabelProps={{ shrink: true }}
            sx={commonSx}
          />
        );

      case 'BOOLEAN':
        return (
          <FormControlLabel
            key={field.fieldName}
            control={
              <Checkbox
                checked={value === true || value === 'true'}
                onChange={(e) => handleChange(field.fieldName, e.target.checked)}
                disabled={isDisabled}
                sx={{
                  color: colors.textMuted,
                  '&.Mui-checked': { color: '#22c55e' },
                }}
              />
            }
            label={field.displayLabel}
            sx={{ mb: 1, color: colors.textPrimary }}
          />
        );

      case 'AUTOCOMPLETE':
        // Searched on the server as the user types; it used to fall through to a plain text box here, so editing a
        // record offered no list at all (the create form already searched).
        if (field.optionsScript) {
          return (
            <SearchableOptionsField
              key={field.fieldName}
              field={field}
              value={value}
              onChange={handleChange}
              disabled={isDisabled}
              error={error}
              rowData={rowData}
              entityName={entityName}
              sx={commonSx}
            />
          );
        }
      // falls through: an AUTOCOMPLETE with no options script is a plain text box
      default: { // TEXT, LINK, REFERENCE
        // Enforce the real WMS column length at the UI layer (field.maxLength, from
        // entity_field_metadata.max_length) instead of only finding out via a 500 from
        // WMS after submit -- see the matching fix in EntityWizardOverlay.js (the
        // create-form equivalent of this file) for the full rationale.
        const charCount = typeof value === 'string' ? value.length : 0;
        const nearLimit = field.maxLength && charCount >= field.maxLength - 10;
        return (
          <TextField
            key={field.fieldName}
            fullWidth
            size="small"
            required={!!field.isRequired}
            label={field.displayLabel}
            value={value}
            onChange={(e) => handleChange(field.fieldName, e.target.value)}
            disabled={isDisabled}
            error={!!error}
            helperText={error || (nearLimit ? `${charCount}/${field.maxLength} characters` : field.description)}
            placeholder={field.placeholder}
            inputProps={field.maxLength ? { maxLength: field.maxLength } : undefined}
            sx={{
              ...commonSx,
              ...(field.textTransform === 'UPPERCASE' ? { '& .MuiInputBase-input': { ...commonSx['& .MuiInputBase-input'], textTransform: 'uppercase' } } : {}),
            }}
          />
        );
      }
    }
  };

  return (
    <Box
      {...enterAdvanceProps(() => { if (!saving) handleSave(); })}
      sx={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        bgcolor: colors.background,
        zIndex: 10,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 2,
          py: 1.5,
          borderBottom: `1px solid ${colors.border}`,
          bgcolor: colors.surface,
        }}
      >
        <Typography variant="subtitle1" sx={{ color: colors.textPrimary, fontWeight: 600 }}>
          {readOnly ? 'View' : 'Edit'} {entityLabel}
        </Typography>
        <IconButton onClick={onClose} sx={{ color: colors.textSecondary }}>
          <CloseIcon />
        </IconButton>
      </Box>

      {/* Form body */}
      <Box sx={{ flex: 1, overflow: 'auto', p: 3, maxWidth: 600 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress size={32} sx={{ color: '#22c55e' }} />
          </Box>
        ) : (
          fields.map((field) => renderField(field))
        )}
      </Box>

      {/* Footer */}
      {!readOnly && !loading && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 1,
            px: 2,
            py: 1.5,
            borderTop: `1px solid ${colors.border}`,
            bgcolor: colors.surface,
          }}
        >
          <Button
            onClick={onClose}
            sx={{ color: colors.textSecondary, borderRadius: 0 }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saving}
            sx={{
              bgcolor: '#22c55e',
              color: colors.background,
              borderRadius: 0,
              '&:hover': { bgcolor: '#16a34a' },
            }}
          >
            {saving ? <CircularProgress size={18} /> : 'Save'}
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default EntityEditOverlay;
